import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { TEAM_OWNER_ACTION } from 'enums';
import { initialTeamState } from 'store/initial-states';
import { fetchAdminTeams, fetchAllMembers } from './extra';
import {
  JoinCode,
  StaffUsageResponse,
  Team,
  TeamMember
} from 'interfaces/team.interface';
import { transformTeamMembersPayload } from 'utils/team.util';

const team = createSlice({
  name: 'team',
  initialState: initialTeamState,
  reducers: {
    updateAreMembersLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.areMembersLoading = payload;
    },
    updateStaffUsage: (
      state,
      { payload }: PayloadAction<StaffUsageResponse | null>
    ) => {
      state.staffUsage = payload;
    },
    updateIsStaffUsageLoading: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isStaffUsageLoading = payload;
    },
    updateAreTeamsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.areTeamsLoading = payload;
    },
    updateIsInvitingUser: (state, { payload }: PayloadAction<boolean>) => {
      state.isInvitingUser = payload;
    },
    updateIsRemovingMember: (state, { payload }: PayloadAction<boolean>) => {
      state.isRemovingMember = payload;
    },
    updateIsAssigningAdmin: (state, { payload }: PayloadAction<boolean>) => {
      state.isAssigningAdmin = payload;
    },
    updateIsRemovingAdmin: (state, { payload }: PayloadAction<boolean>) => {
      state.isRemovingAdmin = payload;
    },
    updateIsAssigningTeacher: (state, { payload }: PayloadAction<boolean>) => {
      state.isAssigningTeacher = payload;
    },
    updateIsRemovingTeacher: (state, { payload }: PayloadAction<boolean>) => {
      state.isRemovingTeacher = payload;
    },
    updateIsUpdatingMemberRole: (state, { payload }: PayloadAction<boolean>) => {
      state.isUpdatingMemberRole = payload;
    },
    updateIsUpdatingTeamName: (state, { payload }: PayloadAction<boolean>) => {
      state.isUpdatingTeamName = payload;
    },
    updateIsUpdatingClassSessionWindow: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingClassSessionWindow = payload;
    },
    updateIsSessionWindowDrawerOpen: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isSessionWindowDrawerOpen = payload;
    },
    updateIsDeletingTeam: (state, { payload }: PayloadAction<boolean>) => {
      state.isDeletingTeam = payload;
    },
    updateTeamOwnerAction: (
      state,
      { payload }: PayloadAction<TEAM_OWNER_ACTION>
    ) => {
      state.teamOwnerAction = payload;
      state.showTeamOwnerActionModal = true;
    },
    updateShowTeamOwnerActionModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showTeamOwnerActionModal = payload;
    },
    updateSelectedTeam: (state, { payload }: PayloadAction<Team | null>) => {
      if (payload?.id !== state.selectedTeam?.id) {
        state.membersLoadedTeamId = null;
      }
      state.selectedTeam = payload;
    },
    updateTeams: (state, { payload }: PayloadAction<Team[]>) => {
      state.teams = payload;
    },
    updateIsAcceptingInvitation: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isAcceptingInvitation = payload;
    },
    updateIsInvitationAccepted: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isInvitationAccepted = payload;
    },
    updateIsUpdatingExpiryDate: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isUpdatingExpiryDate = payload;
    },
    updateSelectedMemberKey: (
      state,
      { payload }: PayloadAction<string | undefined>
    ) => {
      state.selectedMemberKey = payload;
    },
    updateSelectedMembersKeys: (
      state,
      { payload }: PayloadAction<string[]>
    ) => {
      state.selectedMembersKeys = payload;
    },
    updateSearchMember: (state, { payload }: PayloadAction<string>) => {
      state.searchMember = payload;
    },
    updateShowTeamOwnerMassUpdateModal: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.showTeamOwnerMassUpdateModal = payload;
    },
    updateIsExpiryDateMassUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isExpiryDateMassUpdating = payload;
    },
    updateIsMemberTypeMassUpdating: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isMemberTypeMassUpdating = payload;
    },
    updateInvitationLinkHasCustomError: (
      state,
      { payload }: PayloadAction<{ hasError: boolean; message: string }>
    ) => {
      state.invitationLinkHasCustomError = payload;
      if (payload.hasError) {
        state.isAcceptingInvitation = false;
      }
    },
    updateIsAddingMemberManually: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      state.isAddingMemberManually = payload;
    },
    updateMemberDetailsModal: (
      state,
      { payload }: PayloadAction<{ visible: boolean; member?: TeamMember }>
    ) => {
      state.memberDetailsModal = payload;
    },
    updateColumnToSortBy: (state, { payload }: PayloadAction<string>) => {
      state.columnToSortBy = payload;
    },
    updateJoinCodes: (state, { payload }: PayloadAction<JoinCode[]>) => {
      state.joinCodes = payload;
    },
    updateAreJoinCodesLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.areJoinCodesLoading = payload;
    },
    updateIsCreatingJoinCode: (state, { payload }: PayloadAction<boolean>) => {
      state.isCreatingJoinCode = payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAdminTeams.fulfilled, (state, { payload }) => {
        state.teams = Array.isArray(payload) ? payload : [];
        state.areTeamsLoading = false;
        state.teamsInitialized = true;
      })
      .addCase(fetchAllMembers.fulfilled, (state, { payload, meta }) => {
        if (!payload) {
          state.areMembersLoading = false;
          return;
        }
        state.teamMembers = transformTeamMembersPayload(payload);
        state.areMembersLoading = false;
        state.membersLoadedTeamId = meta.arg;
      })
      .addCase(fetchAdminTeams.rejected, (state) => {
        state.teams = [];
        state.areTeamsLoading = false;
        state.teamsInitialized = true;
      });
  }
});

export const {
  updateAreMembersLoading,
  updateStaffUsage,
  updateIsStaffUsageLoading,
  updateAreTeamsLoading,
  updateIsInvitingUser,
  updateIsRemovingMember,
  updateIsAssigningAdmin,
  updateIsRemovingAdmin,
  updateIsAssigningTeacher,
  updateIsRemovingTeacher,
  updateIsUpdatingMemberRole,
  updateIsUpdatingTeamName,
  updateIsUpdatingClassSessionWindow,
  updateIsSessionWindowDrawerOpen,
  updateIsDeletingTeam,
  updateTeamOwnerAction,
  updateShowTeamOwnerActionModal,
  updateSelectedTeam,
  updateTeams,
  updateIsAcceptingInvitation,
  updateIsInvitationAccepted,
  updateIsUpdatingExpiryDate,
  updateSelectedMemberKey,
  updateSelectedMembersKeys,
  updateSearchMember,
  updateShowTeamOwnerMassUpdateModal,
  updateIsExpiryDateMassUpdating,
  updateIsMemberTypeMassUpdating,
  updateInvitationLinkHasCustomError,
  updateIsAddingMemberManually,
  updateMemberDetailsModal,
  updateColumnToSortBy,
  updateJoinCodes,
  updateAreJoinCodesLoading,
  updateIsCreatingJoinCode
} = team.actions;

export default team.reducer;
